import { WebpushServiceApi } from './WebpushServiceApi';
import { Url } from '../url';
import { Environment } from "../../../environment/environment";
import * as braze from '@braze/web-sdk';
import { CurrentUser } from "../user/currentUser";
import { WebPushServiceWorker } from "./WebPushServiceWorker";

export class NotificationsWrap {
    public static async isWebPushSubscribed(): Promise<boolean> {
        if(Environment.isSocial()) {
            return false;
        }

        try {
            const isExistsOnWebpushService = await WebpushServiceApi.isActiveAccount();
            if (!isExistsOnWebpushService) {
                return false;
            }

            return this.hasBrowserGrantedPushes();
        } catch (exception) {
            this.logException(exception, '[Braze] error on getting the subscribe state');
            return false;
        }
    }

    public static async unregisterFromWebPush(): Promise<boolean> {
        if(Environment.isSocial()) {
            return false;
        }

        try {
            const response = await WebpushServiceApi.setAccountState(false);
            if (!response.isUpdated) {
                return false;
            }

            return await this.unregisterForProviderExternally();
        } catch (exception) {
            this.logException(exception, '[Braze] error on unregister from webpush');
            return false;
        }
    }

    public static async subscribeToPushNotifies(): Promise<boolean> {
        if(Environment.isSocial()) {
            return false;
        }

        try {
            const response = await WebpushServiceApi.setAccountState(true);
            if (!response.isUpdated) {
                return false;
            }

            return await this.registerForProviderExternally();
        } catch (exception) {
            this.logException(exception, '[Braze] registerFoWebPush is fault');
            return false;
        }
    }

    public static async registerForProviderExternally(): Promise<boolean> {
        if(Environment.isSocial()) {
            return false;
        }


        try {
            const isRegistered = await WebPushServiceWorker.register();
            if (!isRegistered) {
                return false;
            }

            return new Promise((resolve) => {
                braze.requestPushPermission(() => {
                    console.log('[Braze] Permission requested success');
                    resolve(true);
                }, () => {
                    console.log('[Braze] Permission requested failed');
                    resolve(false);
                })
            });
        } catch (exception) {
            this.logException(exception, '[Braze] Failed on register push for provider externally');
            return false;
        }
    }

    public static async unregisterForProviderExternally(): Promise<boolean> {
        try {
            const isRemoved = await WebPushServiceWorker.remove();
            if (!isRemoved) {
                return false;
            }

            return new Promise((resolve) => {
                braze.unregisterPush(() => {
                    console.log('[Braze] Unregister requested success');
                    resolve(true);
                }, () => {
                    console.log('[Braze] Unregister requested failed');
                    resolve(false);
                })
            });
        } catch (exception) {
            this.logException(exception, '[Braze] Failed on unregister by custom approach');
            return false;
        }
    }

    public static async startSession(userId: string): Promise<void> {
        if (Environment.isSocial()) {
            console.info('Cant start session in Socials');
            return;
        }

        if (CurrentUser.getInstance().isSimulate()) {
            console.info('Cant start session because Spoofer is enabled');
            return;
        }

        const isBrazeInitialized = braze.initialize(window.app.config.notifyProvider.sdkApiKey, {
            enableLogging: true,
            baseUrl: window.app.config.notifyProvider.sdkEndpoint,
            serviceWorkerLocation: this.getServiceWorkerUrl(),
            manageServiceWorkerExternally: true
        });

        if (!isBrazeInitialized) {
            return;
        }

        const currentUser = CurrentUser.getInstance();
        const brazeUser = braze.getUser();

        braze.changeUser(userId);
        brazeUser.setEmail(currentUser.getEmail());

        braze.openSession();
    }

    public static hasBrowserGrantedPushes(): boolean {
        if(Environment.isSocial()) {
            return false;
        }

        return braze.isPushSupported() && !braze.isPushBlocked();
    }

    public static getServiceWorkerUrl(): string {
        return Url.getServiceWorkerUrl('static/j/firebase-messaging-sw.js');
    }

    private static logException(exception: Error, message: string): void {
        console.error(message, {
            exception: {
                message: exception.message,
                stack: exception.stack,
                name: exception.name
            }
        });
        window.Sentry.captureException(exception);
    }
}
